import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { sanitizeUsername, isValidUsername, getUsernameError } from '@/lib/usernameValidation';

const monoFont = "'IBM Plex Mono', monospace";

interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  site_name: string;
  plan: string;
  status: string;
  notes: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  billing_email: string | null;
  is_internal?: boolean;
}

interface Props {
  user: UserProfile;
  onClose: () => void;
  onSaved: () => void;
}

export function UserEditModal({ user, onClose, onSaved }: Props) {
  const [siteName, setSiteName] = useState(user.site_name);
  const [username, setUsername] = useState(user.username);
  const [plan, setPlan] = useState(user.plan);
  const [status, setStatus] = useState(user.status);
  const [isInternal, setIsInternal] = useState(user.is_internal ?? false);
  const [notes, setNotes] = useState(user.notes || '');
  const [saving, setSaving] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  useEffect(() => {
    if (!username || !isValidUsername(username) || username === user.username) {
      setUsernameAvailable(username === user.username ? true : null);
      return;
    }
    setCheckingUsername(true);
    const timeout = setTimeout(async () => {
      const { data } = await supabase.rpc('check_username_available', { p_username: username });
      setUsernameAvailable(!!data);
      setCheckingUsername(false);
    }, 400);
    return () => clearTimeout(timeout);
  }, [username, user.username]);

  const handleUsernameInput = (val: string) => {
    setUsername(sanitizeUsername(val).slice(0, 20));
  };

  const handleSave = async () => {
    setSaving(true);
    await supabase.from('user_profiles').update({
      site_name: siteName,
      username,
      plan,
      status,
      notes: notes || null,
      is_internal: isInternal,
    }).eq('id', user.id);
    setSaving(false);
    onSaved();
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
    letterSpacing: '0.08em', color: 'rgba(247,246,242,0.5)', marginBottom: 6, display: 'block',
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 14, fontWeight: 300, width: '100%',
    padding: '10px 12px', border: '0.5px solid rgba(247,246,242,0.1)',
    backgroundColor: 'rgba(247,246,242,0.04)', color: 'rgba(247,246,242,0.85)',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle, appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='rgba(247,246,242,0.4)'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: 32,
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.7)',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        backgroundColor: '#1a1a1a', padding: 32, maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(247,246,242,0.4)', marginBottom: 24 }}>
          EDITAR USUÁRIO
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <label style={labelStyle}>Nome do site</label>
            <input value={siteName} onChange={e => setSiteName(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Username</label>
            <input value={username} onChange={e => handleUsernameInput(e.target.value)} style={inputStyle} />
            <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'rgba(247,246,242,0.3)' }}>criaaa.com/{username || '...'}</span>
              {username.length > 0 && getUsernameError(username) ? (
                <span style={{ color: '#FF3F2D' }}>{getUsernameError(username)}</span>
              ) : isValidUsername(username) && username !== user.username ? (
                <span style={{ color: checkingUsername ? 'rgba(247,246,242,0.3)' : usernameAvailable ? '#00E87A' : '#FF3F2D' }}>
                  {checkingUsername ? 'verificando...' : usernameAvailable ? 'Disponível ✓' : 'Este endereço já está em uso'}
                </span>
              ) : null}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Plano</label>
            <select value={plan} onChange={e => setPlan(e.target.value)} style={selectStyle}>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} style={selectStyle}>
              <option value="active">Ativo</option>
              <option value="suspended">Suspenso</option>
              <option value="cancelled">Cancelado</option>
              <option value="pending">Pendente</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="isInternal"
              checked={isInternal}
              onChange={e => setIsInternal(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: '#00E87A', cursor: 'pointer' }}
            />
            <label htmlFor="isInternal" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>Conta interna</label>
          </div>
          <div>
            <label style={labelStyle}>Notas internas</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
            />
          </div>

          {/* Stripe fields (read-only) */}
          <div style={{ borderTop: '0.5px solid rgba(247,246,242,0.08)', paddingTop: 20 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Stripe Customer ID</label>
              <div style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: 'rgba(247,246,242,0.3)' }}>
                {user.stripe_customer_id || '—'}
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Stripe Subscription ID</label>
              <div style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: 'rgba(247,246,242,0.3)' }}>
                {user.stripe_subscription_id || '—'}
              </div>
            </div>
            <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.2)', marginTop: 8 }}>
              Integração Stripe pendente
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
          <button onClick={onClose}
            style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '8px 16px', border: '0.5px solid rgba(247,246,242,0.15)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.5)', cursor: 'pointer' }}>
            CANCELAR
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '8px 16px', border: '0.5px solid rgba(247,246,242,0.3)', backgroundColor: 'transparent', color: '#F7F6F2', cursor: saving ? 'wait' : 'pointer' }}>
            {saving ? 'SALVANDO...' : 'SALVAR'}
          </button>
        </div>
      </div>
    </div>
  );
}
